import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Polyline, Tooltip as LeafletTooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { ArrowLeft, Fuel, AlertTriangle, CheckCircle2, Car, User, CalendarCheck, MapPin } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { fmtDate, fmtDateTime as fmt } from "../lib/format";

export default function TripDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [route, setRoute] = useState(null);
  const [origin, setOrigin] = useState(null);

  useEffect(() => {
    api.get(`/trips/${id}`).then((r) => setData(r.data)).catch(() => setError("Log perjalanan tidak dijumpai"));
  }, [id]);

  const booking = data?.booking;
  const [dest, setDest] = useState(null);
  useEffect(() => {
    if (!data) return;
    const t = data.trip;
    const geocode = async () => {
      if (booking?.dest_lat && booking?.dest_lng) return { lat: booking.dest_lat, lng: booking.dest_lng };
      const parts = t.destination.split(",").map((s) => s.trim()).filter(Boolean);
      const queries = [t.destination, ...parts.slice().reverse()];
      for (const q of queries) {
        try {
          const r = await api.get("/geo/search", { params: { q } });
          if (r.data?.[0]) return r.data[0];
        } catch { /* try next */ }
      }
      return null;
    };
    Promise.all([api.get("/locations").then((r) => r.data).catch(() => []), geocode()]).then(([locs, d]) => {
      const o = locs.find((l) => l.id === booking?.origin_location_id) || locs.find((l) => l.is_hq) || locs[0];
      if (!o || !d) return;
      setOrigin(o);
      setDest(d);
      api.get("/geo/route", { params: { from_lat: o.lat, from_lng: o.lng, to_lat: d.lat, to_lng: d.lng, geometry: true } })
        .then((res) => setRoute(res.data))
        .catch(() => {});
    });
  }, [data?.trip?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return <p className="text-sm text-[#DC3545]">{error}</p>;
  if (!data) return <p className="text-sm text-muted-foreground">Memuatkan...</p>;

  const { trip: t, booking: b, vehicle: v } = data;
  const costPerKm = t.distance_km > 0 && t.fuel_cost_rm > 0 ? (t.fuel_cost_rm / t.distance_km).toFixed(2) : null;

  return (
    <div className="space-y-6 fade-up" data-testid="trip-detail-page">
      <div className="flex flex-wrap items-center gap-4">
        <Button variant="outline" size="sm" onClick={() => navigate("/log-perjalanan")} data-testid="back-to-trips-btn">
          <ArrowLeft className="h-4 w-4 mr-2" /> Kembali
        </Button>
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight">Log Perjalanan — {t.destination}</h1>
            {t.kmpl != null && <Badge className="bg-[#10056E] hover:bg-[#10056E]" data-testid="trip-detail-kmpl-badge">{t.kmpl} Km/L</Badge>}
          </div>
          <p className="text-sm text-muted-foreground mt-1">{fmtDate(t.start_datetime)} · {t.vehicle_reg} · {t.driver_name}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Fuel usage card */}
        <Card className="shadow-sm rounded-xl border border-indigo-200 bg-indigo-50/40">
          <CardHeader>
            <CardTitle className="text-base font-semibold flex items-center gap-2"><Fuel className="h-4 w-4" /> Penggunaan Bahan Api</CardTitle>
          </CardHeader>
          <CardContent>
            {t.kmpl != null ? (
              <p className="text-5xl font-bold tracking-tight" data-testid="trip-detail-kmpl">
                {t.kmpl} <span className="text-lg font-medium text-muted-foreground">Km/L</span>
              </p>
            ) : (
              <p className="text-2xl font-semibold text-muted-foreground" data-testid="trip-detail-kmpl">Tiada data</p>
            )}
            <p className="text-sm mt-4">
              {t.kmpl == null
                ? "Tiada pembelian bahan api direkodkan untuk perjalanan ini."
                : "Kadar Km/Liter dikira secara automatik daripada jarak perjalanan dan jumlah bahan api dibeli, mengikut format Buku Log WP 4.1."}
            </p>
          </CardContent>
        </Card>

        {/* Calc breakdown */}
        <Card className="border-slate-200 shadow-sm rounded-xl">
          <CardHeader>
            <CardTitle className="text-base font-semibold">Pengiraan Automatik</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="space-y-3 text-sm">
              {[
                ["Odometer Mula", `${Number(t.odometer_start).toLocaleString()} km`],
                ["Odometer Akhir", `${Number(t.odometer_end).toLocaleString()} km`],
                ["Jarak Perjalanan", `${t.distance_km} km`],
                ["Bahan Api Dibeli", `${t.fuel_liters} L`],
                ["Kos Bahan Api", `RM ${Number(t.fuel_cost_rm).toFixed(2)}`],
                ["Kos / KM", costPerKm ? `RM ${costPerKm}` : "-"],
                ["Kadar Penggunaan", t.kmpl ? `${t.kmpl} Km/L` : "-"],
              ].map(([k, val]) => (
                <div key={k} className="flex justify-between gap-4 border-b last:border-0 pb-2 last:pb-0">
                  <dt className="text-muted-foreground">{k}</dt>
                  <dd className="font-semibold">{val}</dd>
                </div>
              ))}
            </dl>
          </CardContent>
        </Card>

        {/* Trip info */}
        <div className="space-y-6">
          <Card className="border-slate-200 shadow-sm rounded-xl">
            <CardHeader>
              <CardTitle className="text-base font-semibold flex items-center gap-2"><CalendarCheck className="h-4 w-4" /> Maklumat Perjalanan</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="space-y-3 text-sm">
                {[
                  ["Mula", fmt(t.start_datetime)],
                  ["Tamat", fmt(t.end_datetime)],
                  ["Destinasi", t.destination],
                  ["Tujuan", t.purpose],
                  ["Catatan", t.notes || "-"],
                ].map(([k, val]) => (
                  <div key={k} className="flex justify-between gap-4 border-b last:border-0 pb-2 last:pb-0">
                    <dt className="text-muted-foreground shrink-0">{k}</dt>
                    <dd className="font-semibold text-right">{val}</dd>
                  </div>
                ))}
              </dl>
            </CardContent>
          </Card>
          <Card className="border-slate-200 shadow-sm rounded-xl">
            <CardHeader>
              <CardTitle className="text-base font-semibold flex items-center gap-2"><Car className="h-4 w-4" /> Kenderaan & Staf</CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-3">
              <div>
                <p className="font-bold">{t.vehicle_reg}</p>
                {v && <p className="text-muted-foreground">{v.make} {v.model} · {v.fuel_type}</p>}
              </div>
              <div className="flex items-center gap-2 border-t pt-3">
                <User className="h-4 w-4 text-muted-foreground" />
                <p className="font-semibold">{t.driver_name}</p>
              </div>
              <div className="flex gap-2 pt-1">
                {b && (
                  <Link to={`/tempahan/${b.id}`} className="flex-1">
                    <Button variant="outline" size="sm" className="w-full" data-testid="view-booking-detail-btn">Lihat Tempahan</Button>
                  </Link>
                )}
                {user?.role !== "driver" && v && (
                  <Link to={`/kenderaan/${v.id}`} className="flex-1">
                    <Button variant="outline" size="sm" className="w-full" data-testid="view-vehicle-detail-btn">Lihat Kenderaan</Button>
                  </Link>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Route map */}
      {dest && origin && (
        <Card className="border-slate-200 shadow-sm rounded-xl" data-testid="trip-map-card">
          <CardHeader>
            <CardTitle className="text-base font-semibold flex items-center gap-2"><MapPin className="h-4 w-4" /> Peta Perjalanan</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg overflow-hidden border" data-testid="trip-route-map">
              <MapContainer bounds={[[origin.lat, origin.lng], [dest.lat, dest.lng]]} boundsOptions={{ padding: [40, 40] }}
                scrollWheelZoom={false} style={{ height: 340, width: "100%", zIndex: 0 }}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
                {route?.geometry ? (
                  <Polyline positions={route.geometry} pathOptions={{ color: "#10056E", weight: 4, opacity: 0.85 }} />
                ) : (
                  <Polyline positions={[[origin.lat, origin.lng], [dest.lat, dest.lng]]} pathOptions={{ color: "#10056E", weight: 2.5, dashArray: "6 8" }} />
                )}
                <CircleMarker center={[origin.lat, origin.lng]} radius={9} pathOptions={{ color: "#198754", fillColor: "#198754", fillOpacity: 0.85 }}>
                  <LeafletTooltip direction="top" offset={[0, -8]}>Mula: {origin.name}</LeafletTooltip>
                </CircleMarker>
                <CircleMarker center={[dest.lat, dest.lng]} radius={9} pathOptions={{ color: "#DC3545", fillColor: "#DC3545", fillOpacity: 0.85 }}>
                  <LeafletTooltip direction="top" offset={[0, -8]}>Destinasi: {t.destination}</LeafletTooltip>
                </CircleMarker>
              </MapContainer>
            </div>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-muted-foreground mt-2.5">
              <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-[#198754]" /> Mula: <b className="text-slate-700">{origin.name}</b></span>
              <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-[#DC3545]" /> Destinasi: <b className="text-slate-700">{t.destination}</b></span>
              {route && <span>Laluan pandu: <b className="text-slate-700">{route.distance_km} km · ~{route.duration_min} min</b></span>}
              <span>Jarak odometer direkod: <b className="text-slate-700">{t.distance_km} km</b></span>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
